import React, { FC, memo, useMemo, useEffect, lazy, Suspense } from 'react'

import { useSetFocus } from '@please/lrud'

import { getCalendarClassic } from '@/actions/actions'
import { CalendarList } from '@/components/CalendarList/CalendarList'
import { Header } from '@/components/Header'
import { Loader } from '@/components/Loader'
import { PageFallback } from '@/components/PageFallback/PageFallback'
import { focusIds, orientation } from '@/constants/focus'
import { popupStatus } from '@/constants/popup'
import { scrollOptions } from '@/constants/scrollers'
import { useComponentLoadingState } from '@/hooks'
import { useCustomFocus } from '@/hooks/useCustomFocus'
import useStore from '@/store/useStore'
import { LinesGroupType } from '@/types/sliderTypes'
const LinesGroup: FC<LinesGroupType> = lazy(
  () => import('@/components/LinesGroup/LinesGroup'),
)

import { HeaderWrapper } from './styles'
import { Container } from '../styles'

const focusId = `${focusIds.CALENDAR}${focusIds.CONTAINER_MOD}`

const CalendarRaw: FC = () => {
  const setFocus = useSetFocus()
  const calendar = useStore((state) => state.common.calendar)
  const calendarIndex = useStore((state) => state.common.calendarIndex)

  const { pagePopupStatus } = useStore((state) => state.popup)
  const event = useStore((state) => state.common.currentEvent)
  const listPopup = useStore((state) => state.common.listPopup)
  const isEventPopupOpen = Boolean(event)
  const isPagePopupOpen = pagePopupStatus !== popupStatus.CLOSED

  const hasPopup = isPagePopupOpen || isEventPopupOpen || !!listPopup
  const { isLoading, hasError } = useComponentLoadingState('calendar')

  const { defaultFocusChild, onMove } = useCustomFocus({
    focusId,
    startFocusChild: 1,
    needSetFocus: !hasPopup,
  })

  useEffect(() => {
    setFocus(focusId)
    void getCalendarClassic()
  }, [])

  const memoizedLines = useMemo(() => {
    return calendar[calendarIndex]?.lines || []
  }, [calendar, calendarIndex])

  return (
    <Container
      focusId={focusId}
      elementType="div"
      orientation={orientation.VERTICAL}
      defaultFocusChild={defaultFocusChild}
      onMove={onMove}
      className="content-wrapper"
    >
      {isLoading && <Loader />}
      {hasError && !isLoading && (
        <PageFallback reloadHandler={getCalendarClassic} height="100%" />
      )}
      {!hasError && !isLoading && (
        <>
          <HeaderWrapper>
            <CalendarList />
            <Header />
          </HeaderWrapper>
          {!!memoizedLines.length && (
            <Suspense>
              <LinesGroup
                groupData={memoizedLines}
                scrollOptions={scrollOptions.CalendarScroll}
              />
            </Suspense>
          )}
        </>
      )}
    </Container>
  )
}

export const Calendar = memo(CalendarRaw)
